import { toast } from 'react-toastify';
import { CreateManufacturerBody, Manufacturer, UpdateManufacturerBody } from '@core/models/manufacturer.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ManufacturerService from 'services/api/manufacturer.service';

// -------------------------------------------------------------------------

interface IManufacturerState {
    loading: boolean;
    createLoading: boolean;
    updateLoading: boolean;
    deleteLoading: boolean;
    getByIdLoading: boolean;
    error: IErrorModel | undefined;
    manufacturers: Manufacturer[];
    manufacturer: Manufacturer | null;
}

const initialState: IManufacturerState = {
    loading: false,
    createLoading: false,
    updateLoading: false,
    getByIdLoading: false,
    deleteLoading: false,
    error: {} as IErrorModel,
    manufacturers: [],
    manufacturer: null,
};

// -------------------------------------------------------------------------

export const getManufacturersThunk = createAsyncThunk<Manufacturer[], void, { rejectValue: IErrorModel }>(
    `/manufacturer/getManufacturers`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await ManufacturerService.getManufacturers();
            return response.data.manufacturers
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch manufacturers', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const getManufacturerByIdThunk = createAsyncThunk<Manufacturer, number, { rejectValue: IErrorModel }>(
    `/manufacturer/getManufacturerById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await ManufacturerService.getManufacturerById(id);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch manufacturer', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const createManufacturerThunk = createAsyncThunk<
    ISuccessResponse,
    CreateManufacturerBody,
    { rejectValue: IErrorModel }
>(`/manufacturer/createManufacturer`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await ManufacturerService.createManufacturer(body);
        toast.success(response.data.message || 'Manufacturer created successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to create manufacturer', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const updateManufacturerThunk = createAsyncThunk<
    ISuccessResponse,
    UpdateManufacturerBody,
    { rejectValue: IErrorModel }
>(`/manufacturer/updateManufacturer`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await ManufacturerService.updateManufacturer(body);
        toast.success(response.data.message || 'Manufacturer updated successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to update manufacturer', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const deleteManufacturerThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/manufacturer/deleteManufacturer`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await ManufacturerService.deleteManufacturer(id);
            toast.success(response.data.message || 'Manufacturer deleted successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to delete manufacturer', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

// -------------------------------------------------------------------------

const manufacturerSlice = createSlice({
    name: `manufacturer`,
    initialState,
    reducers: {
        clearManufacturer(state) {
            state.manufacturer = null;
        },
    },
    extraReducers: (builder) => {
        // Get Manufacturers
        builder.addCase(getManufacturersThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getManufacturersThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.manufacturers = action.payload;
        });
        builder.addCase(getManufacturersThunk.rejected, (state, action) => {
            state.loading = false;
            state.manufacturers = [];
            state.error = action.payload;
        });

        // Get Manufacturer By Id
        builder.addCase(getManufacturerByIdThunk.pending, (state) => {
            state.getByIdLoading = true;
        });
        builder.addCase(getManufacturerByIdThunk.fulfilled, (state, action) => {
            state.getByIdLoading = false;
            state.manufacturer = action.payload;
        });
        builder.addCase(getManufacturerByIdThunk.rejected, (state, action) => {
            state.getByIdLoading = false;
            state.error = action.payload;
        });

        // Create Manufacturer
        builder.addCase(createManufacturerThunk.pending, (state) => {
            state.createLoading = true;
        });
        builder.addCase(createManufacturerThunk.fulfilled, (state) => {
            state.createLoading = false;
        });
        builder.addCase(createManufacturerThunk.rejected, (state, action) => {
            state.createLoading = false;
            state.error = action.payload;
        });

        // Update Manufacturer
        builder.addCase(updateManufacturerThunk.pending, (state) => {
            state.updateLoading = true;
        });
        builder.addCase(updateManufacturerThunk.fulfilled, (state) => {
            state.updateLoading = false;
        });
        builder.addCase(updateManufacturerThunk.rejected, (state, action) => {
            state.updateLoading = false;
            state.error = action.payload;
        });

        // Delete Manufacturer
        builder.addCase(deleteManufacturerThunk.pending, (state) => {
            state.deleteLoading = true;
        });
        builder.addCase(deleteManufacturerThunk.fulfilled, (state) => {
            state.deleteLoading = false;
        });
        builder.addCase(deleteManufacturerThunk.rejected, (state, action) => {
            state.deleteLoading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default manufacturerSlice;
export const { clearManufacturer } = manufacturerSlice.actions;
export const manufacturerReducer = manufacturerSlice.reducer;

